import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput } from '../BubbleUI';
import { updateConfigurations } from '@go-client/lib/api/property';
import { PropertySettings } from '@go-shared/types/property';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { createPortal } from 'react-dom';

interface NaveganteFormDataBase {
  config: PropertySettings;
}

type NaveganteFormData = NaveganteFormDataBase & {
  _dirty: boolean;
  _errors: Partial<NaveganteFormError>;
};

type NaveganteFormError = { [K in keyof NaveganteFormDataBase]: string };

function DEFAULT_FORM_DATA(propConfigs?: PropertySettings): NaveganteFormData {
  return {
    config: { ...propConfigs },
    _dirty: false,
    _errors: {},
  };
}

interface NaveganteFormProps {
  edit?: PropertySettings;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function NaveganteForm(props: NaveganteFormProps) {
  const { activeProperty } = useActiveProperty();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<NaveganteFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof PropertySettings>(key: T, val: PropertySettings[T]) {
    setFormData({ ...formData, _dirty: true, config: { ...formData.config, [key]: val } });
  }

  async function submitForm() {
    setSaving(true);
    try {
      await updateConfigurations(activeProperty._id, formData.config);
      await props.onFormSubmitted();
    } finally {
      setSaving(false);
    }
  }

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Days to View" className="col-span-3">
        <div className="flex items-start">
          <BubbleInput
            type="checkbox"
            checked={formData.config.naveganteMobileDayCap !== undefined}
            onValue={check => {
              changeForm('naveganteMobileDayCap', check ? 3 : undefined);
            }}
            className="px-0-i pt-2 mr-2 leading-none"
          />
          <BubbleInput
            type="number"
            disabled={formData.config.naveganteMobileDayCap === undefined}
            value={formData.config.naveganteMobileDayCap}
            onValue={val => changeForm('naveganteMobileDayCap', val)}
            min={0}
            max={30}
            block
            className="flex-grow"
          />
        </div>
        <div className="text-slate italic mt-1">
          Limit amount of days of content that Mobile is allowed to go into the future. The default
          is 3.
        </div>
      </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="w-full flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
